import { Link } from 'react-router-dom';
import { ExternalLink, Tag } from 'lucide-react';
import type { ConceptTypeNode } from './ConceptTypeTree';

interface ConceptTypeDetailProps {
  node: ConceptTypeNode | null;
  byId: Map<string, ConceptTypeNode>;
}

export function ConceptTypeDetail({ node, byId }: ConceptTypeDetailProps) {
  if (!node) {
    return (
      <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-sm text-slate-400">
        Select a concept type to view its detail
      </div>
    );
  }

  const data = (node.canonical_data ?? {}) as Record<string, unknown>;
  const displayName =
    typeof data.display_name === 'string' ? data.display_name : node.canonical_name;
  const description = typeof data.description === 'string' ? data.description : null;
  const category = typeof data.type_category === 'string' ? data.type_category : null;
  const taxonomyScope = Array.isArray(data.taxonomy_scope)
    ? (data.taxonomy_scope as string[])
    : [];
  const synonyms = Array.isArray(data.synonyms) ? (data.synonyms as string[]) : [];
  const extractionPatterns = Array.isArray(data.extraction_patterns)
    ? (data.extraction_patterns as string[])
    : [];
  const parentId = typeof data.parent_type_id === 'string' ? data.parent_type_id : null;
  const parent = parentId ? byId.get(parentId) : null;
  const instanceCount =
    typeof data.instance_count === 'number' ? (data.instance_count as number) : 0;
  const derivedFrom = typeof data.derived_from === 'string' ? data.derived_from : null;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h2 className="text-lg font-semibold text-navy-900">{displayName}</h2>
        <div className="flex items-center gap-2 mt-1 text-xs text-slate-500 flex-wrap">
          {category && (
            <span className="px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 font-medium">
              {category.replace(/_/g, ' ')}
            </span>
          )}
          <code className="text-[10px]">{node.canonical_name}</code>
          <span>{instanceCount} instances</span>
          <span>{node.children.length} children</span>
          {derivedFrom && <span>via {derivedFrom}</span>}
        </div>
        {description && (
          <p className="text-sm text-slate-700 mt-3">{description}</p>
        )}
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3">
          Hierarchy
        </h3>
        <div className="space-y-1.5 text-sm">
          {parent ? (
            <Row label="Parent">
              <code className="text-xs">{parent.canonical_name}</code>
            </Row>
          ) : (
            <Row label="Parent">
              <span className="text-slate-400">— (root)</span>
            </Row>
          )}
          <Row label="Children">
            {node.children.length === 0 ? (
              <span className="text-slate-400">none</span>
            ) : (
              <div className="flex flex-wrap gap-1">
                {node.children.slice(0, 12).map((c) => (
                  <span
                    key={c.id}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
                  >
                    {c.canonical_name}
                  </span>
                ))}
                {node.children.length > 12 && (
                  <span className="text-[10px] text-slate-400 italic">
                    +{node.children.length - 12} more
                  </span>
                )}
              </div>
            )}
          </Row>
          {taxonomyScope.length > 0 && (
            <Row label="Taxonomy Scope">
              <div className="flex flex-wrap gap-1">
                {taxonomyScope.slice(0, 6).map((t) => (
                  <span
                    key={t}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-brand-teal/10 text-brand-teal-dark"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </Row>
          )}
        </div>
      </div>

      {(synonyms.length > 0 || extractionPatterns.length > 0) && (
        <div className="bg-white rounded-xl border border-slate-200 p-5">
          <h3 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-1.5">
            <Tag size={12} /> Extraction
          </h3>
          {synonyms.length > 0 && (
            <Row label="Synonyms">
              <div className="flex flex-wrap gap-1">
                {synonyms.map((s) => (
                  <span
                    key={s}
                    className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </Row>
          )}
          {extractionPatterns.length > 0 && (
            <div className="mt-2">
              <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                Patterns
              </div>
              <ul className="text-xs text-slate-600 list-disc list-inside space-y-0.5">
                {extractionPatterns.slice(0, 8).map((p, i) => (
                  <li key={i}>
                    <code>{p}</code>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      <Link
        to={`/knowledge/estate/entities/${node.id}`}
        className="inline-flex items-center gap-1.5 text-xs text-brand-teal hover:text-brand-teal-dark"
      >
        Open canonical entity detail
        <ExternalLink size={11} />
      </Link>
    </div>
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex gap-3 items-start">
      <span className="text-xs text-slate-500 w-28 flex-shrink-0 mt-0.5">{label}</span>
      <div className="flex-1 min-w-0 text-sm text-slate-700">{children}</div>
    </div>
  );
}
